import React, { FC } from 'react';
import { useApp } from 'providers/useApp/AppContext';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import { resetHealthCheck } from 'utils/fnHealthCheck';
import translate from 'utils/fnTranslate';

export const NoInternetAlert: FC<{ onRetryClicked: () => void }> = ({ onRetryClicked }) => (
    <AlertDialog
        title={translate('ERR_NO_INTERNET_TITLE')}
        bodyText={translate('ERR_NO_INTERNET_BODY')}
        buttons={[
            {
                text: translate('TRY_AGAIN_BUTTON'),
                onClick: onRetryClicked,
            },
        ]}
    />
);

export const NetworkErrorAlert: FC<{ onRetryClicked: () => void; networkError: number }> = ({ onRetryClicked, networkError }) => (
    <AlertDialog
        title={translate('ERR_BACKEND_SERVICE_DOWN_TITLE')}
        bodyText={translate('ERR_BACKEND_SERVICE_DOWN_BODY')}
        errorMessage={networkError !== -1 ? `${translate('ERR_CODE')} ${networkError}` : ''}
        buttons={[
            {
                text: translate('TRY_AGAIN_BUTTON'),
                onClick: onRetryClicked,
            },
        ]}
    />
);

type NetworkCheckOptions = {
    keepMounted?: () => boolean;
};

export function withNetworkCheck(Wrapped: React.FC | React.ComponentClass, options: NetworkCheckOptions = {}) {
    return function WrappedWithOverlay(props) {
        const { noInternet, setNoInternet, networkError, setNetworkError } = useApp();

        const showWrapped = (!noInternet && !networkError) || !!options.keepMounted?.();

        const renderWrapped = () => (
            <React.Fragment>
                {showWrapped && <Wrapped {...props} />}
                {noInternet && (
                    <NoInternetAlert
                        onRetryClicked={() => {
                            setNoInternet(false);
                        }}
                    />
                )}
                {networkError && (
                    <NetworkErrorAlert
                        networkError={networkError}
                        onRetryClicked={() => {
                            resetHealthCheck();
                            setNetworkError(null);
                        }}
                    />
                )}
            </React.Fragment>
        );

        return renderWrapped();
    };
}

export const useGlobalNoInternet = () => {
    const { setNoInternet, noInternet, setNetworkError } = useApp();

    return {
        noInternet,
        onNoInternet: () => {
            setNoInternet(true);
            setNetworkError(null);
        },
        onInternet: () => {
            setNoInternet(false);
            setNetworkError(null);
        },
    };
};

export const useGlobalNetworkError = () => {
    const { setNetworkError, networkError, setNoInternet } = useApp();

    return {
        networkError,
        onNetworkError: (errorCode: number) => {
            setNetworkError(errorCode);
            setNoInternet(false);
        },
    };
};
